<template>
  <div class="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
    <div class="glass-strong rounded-xl p-6 w-full max-w-2xl mx-4">
      <h3 class="text-xl font-bold text-white mb-4">Check Out Equipment</h3>
      <div class="flex items-center space-x-2 text-slate-400 text-sm mb-4">
        <span :class="step >= 1 ? 'text-white' : ''">1. Assignment</span>
        <span>›</span>
        <span :class="step >= 2 ? 'text-white' : ''">2. Customer</span>
        <span>›</span>
        <span :class="step >= 3 ? 'text-white' : ''">3. Products & Shipping</span>
      </div>

      <form @submit.prevent="submit">
        <div v-if="step === 1" class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm text-slate-300 mb-1">Purpose</label>
            <select
              v-model="purpose"
              required
              class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
            >
              <option value="poc">Proof of Concept</option>
              <option value="test">Test</option>
              <option value="tech_eval">Tech Eval</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
          <div>
            <label class="block text-sm text-slate-300 mb-1">Expected Return (optional)</label>
            <input
              v-model="expectedReturnAt"
              type="datetime-local"
              class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
            />
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm text-slate-300 mb-1">Project Name (optional)</label>
            <input
              v-model="projectName"
              placeholder="e.g. McHale Test Project"
              class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
            />
          </div>
          <div class="md:col-span-2 flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2">
            <input id="checkout-watchlist" v-model="watchlist" type="checkbox" class="accent-primary-500" />
            <label for="checkout-watchlist" class="text-sm text-slate-200">Watchlist</label>
            <span class="text-xs text-slate-500">(leave unchecked for normal flow)</span>
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm text-slate-300 mb-1">Usage Notes</label>
            <input
              v-model="usageNotes"
              class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
            />
          </div>
        </div>

        <div v-else-if="step === 2" class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm text-slate-300 mb-1">Organization</label>
            <div class="flex gap-2 mb-2">
              <input
                v-model="orgQuery"
                placeholder="Search organizations"
                class="flex-1 px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <button
                type="button"
                class="px-3 py-2 rounded-lg btn-primary"
                @click="inlineCreateOrg = true"
              >
                New
              </button>
            </div>
            <div v-if="inlineCreateOrg" class="glass p-2 rounded mb-2 space-y-2">
              <input
                v-model="newOrgName"
                placeholder="Org name"
                class="w-full px-2 py-1 bg-slate-800/70 border border-slate-700 rounded text-white"
              />
              <select
                v-model="newOrgType"
                class="w-full px-2 py-1 bg-slate-800/70 border border-slate-700 rounded text-white"
              >
                <option value="customer">Customer</option>
                <option value="prospect">Prospect</option>
                <option value="partner">Partner</option>
                <option value="supplier">Supplier</option>
              </select>
              <div class="flex gap-2">
                <button
                  type="button"
                  class="px-2 py-1 bg-slate-800/70 border border-slate-700 text-white rounded"
                  @click="inlineCreateOrg = false"
                >
                  Cancel
                </button>
                <button type="button" class="px-2 py-1 rounded btn-primary" @click="createOrg">
                  Save
                </button>
              </div>
            </div>
            <select
              v-model.number="organizationId"
              class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
            >
              <option :value="undefined">Select...</option>
              <option v-for="o in filteredOrgs" :key="o.id" :value="o.id">
                {{ o.name }} ({{ o.type }})
              </option>
            </select>
          </div>
          <div>
            <label class="block text-sm text-slate-300 mb-1">Contact</label>
            <div class="flex gap-2 mb-2">
              <input
                v-model="contactQuery"
                placeholder="Search contacts"
                class="flex-1 px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <button
                type="button"
                class="px-3 py-2 rounded-lg btn-primary"
                @click="inlineCreateContact = true"
              >
                New
              </button>
            </div>
            <div v-if="inlineCreateContact" class="glass p-2 rounded mb-2 space-y-2">
              <input
                v-model="newContactName"
                placeholder="Name"
                class="w-full px-2 py-1 bg-slate-800/70 border border-slate-700 rounded text-white"
              />
              <input
                v-model="newContactEmail"
                placeholder="Email"
                class="w-full px-2 py-1 bg-slate-800/70 border border-slate-700 rounded text-white"
              />
              <input
                v-model="newContactPhone"
                placeholder="Phone"
                class="w-full px-2 py-1 bg-slate-800/70 border border-slate-700 rounded text-white"
              />
              <div class="flex gap-2">
                <button
                  type="button"
                  class="px-2 py-1 bg-slate-800/70 border border-slate-700 text-white rounded"
                  @click="inlineCreateContact = false"
                >
                  Cancel
                </button>
                <button type="button" class="px-2 py-1 rounded btn-primary" @click="createContact">
                  Save
                </button>
              </div>
            </div>
            <select
              v-model.number="contactId"
              class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
            >
              <option :value="undefined">Select...</option>
              <option v-for="c in filteredContacts" :key="c.id" :value="c.id">
                {{ c.name }} - {{ c.email }}
              </option>
            </select>
            <button
              type="button"
              class="mt-2 text-xs text-primary-300 hover:text-primary-200 underline"
              @click="goToConsignees"
            >
              Manage Consignees
            </button>
          </div>

          <!-- Recipient Notifications — auto-expanded when contact is selected -->
          <div v-if="contactId && selectedContact" class="col-span-full mt-3 p-3 rounded-lg border border-primary-500/30 bg-primary-500/5">
            <button
              type="button"
              class="flex items-center gap-2 text-sm text-white hover:text-primary-300 transition-colors font-medium"
              @click="showRecipientNotifs = !showRecipientNotifs"
            >
              <svg
                class="w-4 h-4 transition-transform"
                :class="showRecipientNotifs ? 'rotate-90' : ''"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              <svg class="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Recipient Notifications
              <span class="text-[10px] text-slate-400 ml-1">
                ({{ recipientAlertEvents.length }} alert{{ recipientAlertEvents.length !== 1 ? 's' : '' }} enabled)
              </span>
            </button>
            <transition name="slide-fade">
              <div v-if="showRecipientNotifs" class="mt-2 p-3 rounded-lg bg-slate-800/40 border border-slate-700/50 space-y-3">
                <p class="text-[11px] text-slate-400">
                  Choose which notifications <strong class="text-slate-300">{{ selectedContact.name }}</strong> will receive for this checkout.
                </p>
                <!-- Channel toggles -->
                <div class="flex items-center gap-4">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" v-model="recipientChannelEmail" class="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 text-primary-500" />
                    <span class="text-xs text-slate-300">Email</span>
                    <span v-if="selectedContact.email" class="text-[10px] text-slate-500">({{ selectedContact.email }})</span>
                    <span v-else class="text-[10px] text-red-400">No email</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" v-model="recipientChannelSms" class="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 text-green-500" />
                    <span class="text-xs text-slate-300">SMS</span>
                    <span v-if="selectedContact.phone" class="text-[10px] text-slate-500">({{ selectedContact.phone }})</span>
                    <span v-else class="text-[10px] text-red-400">No phone</span>
                  </label>
                </div>
                <!-- Event checkboxes -->
                <div class="grid grid-cols-2 gap-1.5">
                  <label
                    v-for="evt in RECIPIENT_EVENTS"
                    :key="evt.key"
                    class="flex items-center gap-2 p-1.5 rounded bg-slate-800/30 cursor-pointer hover:bg-slate-800/50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      :value="evt.key"
                      v-model="recipientAlertEvents"
                      class="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 text-primary-500"
                    />
                    <span class="text-xs text-slate-300">{{ evt.label }}</span>
                  </label>
                </div>
              </div>
            </transition>
          </div>

          <div v-if="!contactId" class="col-span-full rounded-lg border border-slate-700/60 bg-slate-900/40 p-3 space-y-3">
            <p class="text-xs uppercase tracking-[0.15em] text-slate-400">Or Enter New Consignee</p>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                v-model="newConsigneeName"
                placeholder="Consignee name"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeTitle"
                placeholder="Title"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeEmail"
                placeholder="Email"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneePhone"
                placeholder="Phone"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeOrganization"
                placeholder="Organization (optional)"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeAddress1"
                placeholder="Address 1"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeAddress2"
                placeholder="Address 2"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeCity"
                placeholder="City"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <div class="grid grid-cols-2 gap-3">
                <input
                  v-model="newConsigneeState"
                  placeholder="State"
                  class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white uppercase"
                  maxlength="2"
                />
                <input
                  v-model="newConsigneeZip"
                  placeholder="ZIP"
                  class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
                />
              </div>
            </div>
            <label class="inline-flex items-center gap-2 text-sm text-slate-300">
              <input v-model="newConsigneeShippingSameAsConsignee" type="checkbox" />
              Shipping address same as Consignee address
            </label>
            <div
              v-if="!newConsigneeShippingSameAsConsignee"
              class="grid grid-cols-1 md:grid-cols-2 gap-3"
            >
              <input
                v-model="newConsigneeShippingAddress1"
                placeholder="Shipping Address 1"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeShippingAddress2"
                placeholder="Shipping Address 2"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeShippingCity"
                placeholder="Shipping City"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <div class="grid grid-cols-2 gap-3">
                <input
                  v-model="newConsigneeShippingState"
                  placeholder="Shipping State"
                  class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white uppercase"
                  maxlength="2"
                />
                <input
                  v-model="newConsigneeShippingZip"
                  placeholder="Shipping ZIP"
                  class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-else class="grid grid-cols-1 gap-3">
          <label class="block text-sm text-slate-300">Products</label>
          <input
            v-model="productQuery"
            placeholder="Search products"
            class="mb-2 w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
          />
          <div class="grid grid-cols-2 gap-2 max-h-40 overflow-auto pr-1">
            <label
              v-for="p in filteredProducts"
              :key="p.id"
              class="flex items-center space-x-2 text-slate-200"
            >
              <input type="checkbox" :value="p.id" v-model="productIds" />
              <span>{{ p.name }}</span>
            </label>
          </div>

          <div class="mt-4 border-t border-slate-700 pt-4">
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm text-slate-300">Shipping (optional)</label>
              <span class="text-xs text-slate-500"
                >UPS configured: {{ ratesMeta.upsConfigured ? 'yes' : 'no' }}</span
              >
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p class="text-slate-400 text-xs mb-1">From</p>
                <input
                  v-model="from.line1"
                  placeholder="Address line"
                  class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white mb-2"
                />
                <div class="grid grid-cols-3 gap-2">
                  <input
                    v-model="from.city"
                    placeholder="City"
                    class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
                  />
                  <input
                    v-model="from.state"
                    placeholder="State"
                    class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
                  />
                  <input
                    v-model="from.postal_code"
                    placeholder="ZIP"
                    class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
                  />
                </div>
              </div>
              <div>
                <p class="text-slate-400 text-xs mb-1">To</p>
                <input
                  v-model="to.line1"
                  placeholder="Address line"
                  class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white mb-2"
                />
                <div class="grid grid-cols-3 gap-2">
                  <input
                    v-model="to.city"
                    placeholder="City"
                    class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
                  />
                  <input
                    v-model="to.state"
                    placeholder="State"
                    class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
                  />
                  <input
                    v-model="to.postal_code"
                    placeholder="ZIP"
                    class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
                  />
                </div>
              </div>
            </div>
            <div class="grid grid-cols-4 gap-2 mt-3">
              <input
                v-model.number="parcel.weight_kg"
                type="number"
                min="0"
                step="0.01"
                placeholder="Weight kg"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model.number="parcel.length_cm"
                type="number"
                min="0"
                placeholder="Len cm"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model.number="parcel.width_cm"
                type="number"
                min="0"
                placeholder="Wid cm"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model.number="parcel.height_cm"
                type="number"
                min="0"
                placeholder="Hgt cm"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
            </div>
            <div class="flex items-center gap-2 mt-3">
              <button type="button" @click="fetchRates" class="px-3 py-2 rounded-lg btn-light">
                Get Rates
              </button>
              <span class="text-xs text-slate-400" v-if="ratesFetching">Fetching…</span>
              <span class="text-xs text-red-400" v-if="ratesError">{{ ratesError }}</span>
            </div>
            <div v-if="rates.length" class="mt-3 space-y-2">
              <label
                v-for="r in rates"
                :key="r.rateId"
                class="flex items-center justify-between glass p-2 rounded"
              >
                <div class="flex items-center gap-2">
                  <input type="radio" name="rate" :value="r.rateId" v-model="selectedRateId" />
                  <span class="text-slate-200 text-sm"
                    >{{ r.carrier }} {{ r.service }} · {{ r.etaDays }}d</span
                  >
                </div>
                <span class="text-slate-100 font-medium"
                  >${{ (r.costCents / 100).toFixed(2) }}</span
                >
              </label>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  :disabled="!selectedRateId"
                  @click="purchaseLabel"
                  class="px-3 py-2 rounded-lg btn-primary disabled:opacity-60"
                >
                  Purchase Label
                </button>
                <span class="text-xs text-slate-400" v-if="purchaseMessage">{{
                  purchaseMessage
                }}</span>
                <a
                  v-if="labelUrl"
                  :href="labelUrl"
                  target="_blank"
                  class="text-xs text-primary-400 underline"
                  >Label</a
                >
                <span v-if="trackingNumber" class="text-xs text-slate-300"
                  >Tracking: {{ trackingNumber }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <p v-if="error" class="text-red-400 text-sm mt-2">{{ error }}</p>
        <div class="flex items-center justify-between mt-6">
          <div class="space-x-2">
            <button
              type="button"
              @click="$emit('close')"
              class="px-4 py-2 bg-slate-800/70 border border-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[--ring]"
            >
              Cancel
            </button>
            <button
              v-if="step > 1"
              type="button"
              @click="step--"
              class="px-4 py-2 bg-slate-800/70 border border-slate-700 text-white rounded-lg"
            >
              Back
            </button>
          </div>
          <div class="space-x-2">
            <button
              v-if="step < 3"
              type="button"
              @click="step++"
              class="px-4 py-2 rounded-lg btn-primary"
            >
              Next
            </button>
            <button v-else type="submit" class="px-4 py-2 rounded-lg btn-primary">Check Out</button>
          </div>
        </div>
      </form>
    </div>

    <div v-if="showSaveConsigneePrompt" class="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">
      <div class="glass-strong rounded-xl p-5 w-full max-w-md mx-4">
        <h4 class="text-lg font-semibold text-white">Save as New Consignee?</h4>
        <p class="text-sm text-slate-300 mt-2">
          This checkout includes a new consignee. Save it to the Consignee directory before creating the checkout?
        </p>
        <div class="mt-4 flex justify-end gap-3">
          <button
            type="button"
            class="px-4 py-2 rounded border border-slate-600 text-slate-300 hover:text-white hover:border-slate-400"
            @click="showSaveConsigneePrompt = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="px-4 py-2 rounded btn-primary"
            :disabled="savingConsignee"
            @click="saveConsigneeAndSubmit"
          >
            {{ savingConsignee ? 'Saving...' : 'Save and Check Out' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showDuplicateConsigneePrompt" class="fixed inset-0 bg-black/70 flex items-center justify-center z-[70]">
      <div class="glass-strong rounded-xl p-5 w-full max-w-lg mx-4">
        <h4 class="text-lg font-semibold text-white">Possible Duplicate Consignee</h4>
        <p class="text-sm text-slate-300 mt-2">
          We found existing consignees with the same email or phone. Use an existing entry or create a new one anyway.
        </p>
        <div class="mt-3 max-h-52 overflow-auto space-y-2">
          <button
            v-for="match in duplicateConsigneeMatches"
            :key="match.id"
            type="button"
            class="w-full text-left px-3 py-2 rounded border border-slate-700 bg-slate-900/50 hover:border-primary-500/60"
            @click="useExistingDuplicate(match.id, match.organization_id)"
          >
            <div class="text-sm text-white">{{ match.name || 'Unnamed consignee' }}</div>
            <div class="text-xs text-slate-400">
              {{ match.email || 'No email' }} • {{ match.phone || 'No phone' }}
            </div>
          </button>
        </div>
        <div class="mt-4 flex justify-end gap-3">
          <button
            type="button"
            class="px-4 py-2 rounded border border-slate-600 text-slate-300 hover:text-white hover:border-slate-400"
            @click="cancelDuplicatePrompt"
          >
            Cancel
          </button>
          <button
            type="button"
            class="px-4 py-2 rounded btn-primary"
            :disabled="savingConsignee"
            @click="createNewDuplicateAnyway"
          >
            {{ savingConsignee ? 'Saving...' : 'Create New Anyway' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { createCheckout, fetchShippingRates, purchaseShippingLabel } from '@/lib/appApi'
import { supabase } from '@/lib/supabaseClient'
import { saveCheckoutRecipient, RECIPIENT_ALERT_EVENTS } from '@/lib/alertService'
import { useRouter } from 'vue-router'

const props = defineProps<{ equipmentId: number }>()
const emit = defineEmits(['close', 'saved'])

const step = ref(1)
const purpose = ref<'poc' | 'test' | 'tech_eval' | 'maintenance'>('poc')
const projectName = ref('')
const watchlist = ref(false)
const expectedReturnAt = ref('')
const usageNotes = ref('')
const organizationId = ref<number | undefined>(undefined)
const contactId = ref<number | undefined>(undefined)
const productIds = ref<number[]>([])
const error = ref('')
const orgs = ref<any[]>([])
const contacts = ref<any[]>([])
const products = ref<any[]>([])
const orgQuery = ref('')
const contactQuery = ref('')
const productQuery = ref('')
const filteredOrgs = computed(() =>
  orgs.value.filter((o) => (o.name || '').toLowerCase().includes(orgQuery.value.toLowerCase())),
)
const filteredContacts = computed(() =>
  contacts.value.filter((c) =>
    (c.name || '').toLowerCase().includes(contactQuery.value.toLowerCase()),
  ),
)
const filteredProducts = computed(() =>
  products.value.filter((p) =>
    (p.name || '').toLowerCase().includes(productQuery.value.toLowerCase()),
  ),
)
const inlineCreateOrg = ref(false)
const newOrgName = ref('')
const newOrgType = ref('customer')
const inlineCreateContact = ref(false)
const newContactName = ref('')
const newContactEmail = ref('')
const newContactPhone = ref('')
const showSaveConsigneePrompt = ref(false)
const showDuplicateConsigneePrompt = ref(false)
const duplicateConsigneeMatches = ref<Array<{ id: number; name?: string; email?: string; phone?: string; organization_id?: number | null }>>([])
const savingConsignee = ref(false)
const router = useRouter()

const newConsigneeOrganization = ref('')
const newConsigneeName = ref('')
const newConsigneeTitle = ref('')
const newConsigneeEmail = ref('')
const newConsigneePhone = ref('')
const newConsigneeAddress1 = ref('')
const newConsigneeAddress2 = ref('')
const newConsigneeCity = ref('')
const newConsigneeState = ref('')
const newConsigneeZip = ref('')
const newConsigneeShippingSameAsConsignee = ref(true)
const newConsigneeShippingAddress1 = ref('')
const newConsigneeShippingAddress2 = ref('')
const newConsigneeShippingCity = ref('')
const newConsigneeShippingState = ref('')
const newConsigneeShippingZip = ref('')
const hasManualConsignee = computed(() =>
  Boolean(
    newConsigneeName.value.trim() ||
      newConsigneeEmail.value.trim() ||
      newConsigneePhone.value.trim() ||
      newConsigneeAddress1.value.trim(),
  ),
)

// Recipient notification preferences
const RECIPIENT_EVENTS = RECIPIENT_ALERT_EVENTS
const showRecipientNotifs = ref(false)
const recipientChannelEmail = ref(true)
const recipientChannelSms = ref(false)
const recipientAlertEvents = ref<string[]>(
  RECIPIENT_ALERT_EVENTS.filter((e) => e.default).map((e) => e.key) as string[],
)
/** The currently selected contact object (computed from contactId). */
const selectedContact = computed(() => {
  if (!contactId.value) return null
  return contacts.value.find((c: any) => c.id === contactId.value) ?? null
})

// Shipping state (minimal)
const from = ref({ line1: '', city: '', state: '', postal_code: '', country: 'US' })
const to = ref({ line1: '', city: '', state: '', postal_code: '', country: 'US' })
const parcel = ref({ weight_kg: 1, length_cm: 30, width_cm: 20, height_cm: 10 })
const rates = ref<
  Array<{
    carrier: string
    service: string
    etaDays: number
    costCents: number
    currency: string
    rateId: string
  }>
>([])
const ratesMeta = ref({ upsConfigured: false, fedexConfigured: false })
const ratesFetching = ref(false)
const ratesError = ref('')
const selectedRateId = ref('')
const trackingNumber = ref('')
const labelUrl = ref('')
const purchaseMessage = ref('')
const DATA_UPDATED_EVENT = 'testdrive:data-updated'

async function finalizeCheckout(resolvedContactId?: number, resolvedOrganizationId?: number) {
  try {
    error.value = ''
    const checkout = await createCheckout({
      equipmentId: props.equipmentId,
      organizationId: resolvedOrganizationId ?? organizationId.value,
      contactId: resolvedContactId ?? contactId.value,
      purpose: purpose.value,
      projectName: projectName.value.trim() || undefined,
      watchlist: watchlist.value,
      usageNotes: usageNotes.value,
      productIds: productIds.value,
      expectedReturnAt: expectedReturnAt.value
        ? new Date(expectedReturnAt.value).toISOString()
        : undefined,
    })

    // Save recipient notification preferences if a contact was selected
    const checkoutId = (checkout as any)?.id ?? (checkout as any)?.checkout_id
    const finalContactId = resolvedContactId ?? contactId.value
    const finalContact =
      contacts.value.find((c: any) => c.id === finalContactId) ?? selectedContact.value
    if (checkoutId && finalContactId && finalContact && recipientAlertEvents.value.length > 0) {
      try {
        await saveCheckoutRecipient({
          checkout_id: checkoutId,
          contact_id: finalContactId,
          recipient_email: finalContact.email ?? null,
          recipient_phone: finalContact.phone ?? null,
          recipient_name: finalContact.name ?? null,
          alert_events: recipientAlertEvents.value,
          channel_email: recipientChannelEmail.value,
          channel_sms: recipientChannelSms.value,
        })
      } catch (recipientErr) {
        // Non-blocking: log but don't fail the checkout
        console.warn('[CheckoutWizard] Failed to save recipient notification config:', recipientErr)
      }
    }

    try {
      window.dispatchEvent(new CustomEvent(DATA_UPDATED_EVENT))
    } catch {}
    emit('saved')
    emit('close')
  } catch (e: any) {
    error.value = e.message || 'Failed to check out'
  }
}

async function submit() {
  if (!contactId.value && hasManualConsignee.value) {
    showSaveConsigneePrompt.value = true
    return
  }
  if (!contactId.value && !hasManualConsignee.value) {
    error.value = 'Select a consignee or enter new consignee details.'
    return
  }
  await finalizeCheckout()
}

function normalizePhone(value: string) {
  return value.replace(/\D/g, '')
}

function getDuplicateConsigneeMatches() {
  const email = newConsigneeEmail.value.trim().toLowerCase()
  const phone = normalizePhone(newConsigneePhone.value.trim())
  if (!email && !phone) return []
  return contacts.value.filter((c: any) => {
    const cEmail = String(c.email || '').trim().toLowerCase()
    const cPhone = normalizePhone(String(c.phone || '').trim())
    const emailMatch = Boolean(email && cEmail && cEmail === email)
    const phoneMatch = Boolean(phone && cPhone && cPhone === phone)
    return emailMatch || phoneMatch
  })
}

function cancelDuplicatePrompt() {
  showDuplicateConsigneePrompt.value = false
  duplicateConsigneeMatches.value = []
}

async function useExistingDuplicate(existingContactId: number, existingOrganizationId?: number | null) {
  try {
    savingConsignee.value = true
    showDuplicateConsigneePrompt.value = false
    showSaveConsigneePrompt.value = false
    contactId.value = existingContactId
    await finalizeCheckout(existingContactId, (existingOrganizationId ?? undefined) as number | undefined)
  } catch (e: any) {
    error.value = e?.message || 'Failed to check out with existing consignee.'
  } finally {
    savingConsignee.value = false
  }
}

async function saveConsigneeAndSubmitInternal(skipDuplicateCheck = false) {
  try {
    savingConsignee.value = true
    error.value = ''
    if (!newConsigneeName.value.trim()) {
      error.value = 'Consignee name is required to save.'
      showSaveConsigneePrompt.value = false
      return
    }

    if (!skipDuplicateCheck) {
      const matches = getDuplicateConsigneeMatches()
      if (matches.length > 0) {
        duplicateConsigneeMatches.value = matches
        showDuplicateConsigneePrompt.value = true
        showSaveConsigneePrompt.value = false
        return
      }
    }

    let resolvedOrganizationId = organizationId.value ?? null
    if (!resolvedOrganizationId && newConsigneeOrganization.value.trim()) {
      const { data: orgRow, error: orgError } = await supabase
        .schema('app' as any)
        .from('organizations')
        .insert({ name: newConsigneeOrganization.value.trim(), type: 'customer' })
        .select('id')
        .single()
      if (orgError) throw orgError
      resolvedOrganizationId = orgRow.id
    }
    const { data: contactRow, error: contactError } = await supabase
      .schema('app' as any)
      .from('contacts')
      .insert({
        organization_id: resolvedOrganizationId,
        name: newConsigneeName.value.trim(),
        title: newConsigneeTitle.value.trim() || null,
        email: newConsigneeEmail.value.trim() || null,
        phone: newConsigneePhone.value.trim() || null,
        address1: newConsigneeAddress1.value.trim() || null,
        address2: newConsigneeAddress2.value.trim() || null,
        city: newConsigneeCity.value.trim() || null,
        state: newConsigneeState.value.trim().toUpperCase() || null,
        zip: newConsigneeZip.value.trim() || null,
        shipping_same_as_consignee: newConsigneeShippingSameAsConsignee.value,
        shipping_address1: newConsigneeShippingSameAsConsignee.value
          ? null
          : (newConsigneeShippingAddress1.value.trim() || null),
        shipping_address2: newConsigneeShippingSameAsConsignee.value
          ? null
          : (newConsigneeShippingAddress2.value.trim() || null),
        shipping_city: newConsigneeShippingSameAsConsignee.value
          ? null
          : (newConsigneeShippingCity.value.trim() || null),
        shipping_state: newConsigneeShippingSameAsConsignee.value
          ? null
          : (newConsigneeShippingState.value.trim().toUpperCase() || null),
        shipping_zip: newConsigneeShippingSameAsConsignee.value
          ? null
          : (newConsigneeShippingZip.value.trim() || null),
      })
      .select('id')
      .single()
    if (contactError) throw contactError
    await loadContacts()
    contactId.value = contactRow.id
    showSaveConsigneePrompt.value = false
    await finalizeCheckout(contactRow.id, resolvedOrganizationId ?? undefined)
  } catch (e: any) {
    error.value = e?.message || 'Failed to save consignee.'
    showSaveConsigneePrompt.value = false
    showDuplicateConsigneePrompt.value = false
  } finally {
    savingConsignee.value = false
  }
}

async function saveConsigneeAndSubmit() {
  await saveConsigneeAndSubmitInternal(false)
}

async function createNewDuplicateAnyway() {
  await saveConsigneeAndSubmitInternal(true)
}

async function fetchRates() {
  try {
    ratesError.value = ''
    ratesFetching.value = true
    rates.value = []
    selectedRateId.value = ''
    const res = await fetchShippingRates({
      from: from.value as any,
      to: to.value as any,
      parcel: parcel.value as any,
      carriers: ['UPS'],
    })
    rates.value = res.rates || []
    ratesMeta.value = {
      upsConfigured: res.meta?.upsConfigured ?? false,
      fedexConfigured: res.meta?.fedexConfigured ?? false,
    }
  } catch (e: any) {
    ratesError.value = e.message || 'Failed to fetch rates'
  } finally {
    ratesFetching.value = false
  }
}

async function purchaseLabel() {
  try {
    purchaseMessage.value = 'Purchasing…'
    const res = await purchaseShippingLabel({
      type: 'outbound',
      rateId: selectedRateId.value,
      from: from.value as any,
      to: to.value as any,
    })
    trackingNumber.value = res.shipment.tracking_number
    labelUrl.value = res.shipment.label_url
    purchaseMessage.value = 'Label purchased.'
  } catch (e: any) {
    purchaseMessage.value = e.message || 'Failed to purchase label'
  }
}

async function loadOrgs() {
  const { data } = await supabase.schema('app' as any).from('organizations').select('*').order('name')
  orgs.value = data || []
}
async function loadContacts() {
  const { data } = await supabase.schema('app' as any).from('contacts').select('*').order('name')
  contacts.value = data || []
}
async function loadProducts() {
  const { data } = await supabase.schema('app' as any).from('products').select('*').order('name')
  products.value = data || []
}

onMounted(() => {
  loadOrgs()
  loadContacts()
  loadProducts()
})
onMounted(() => {
  // Autofill from scan
  try {
    const serial = localStorage.getItem('scannedSerial')
    if (serial) {
      // Could look up equipment by serial here for future enhancement
      localStorage.removeItem('scannedSerial')
    }
  } catch {}
})
watch(organizationId, (val) => {
  // optionally filter contacts by organization
})
/** Auto-expand recipient notifications when a contact is first selected. */
watch(contactId, (newVal) => {
  if (newVal) showRecipientNotifs.value = true
})

watch(
  [
    newConsigneeShippingSameAsConsignee,
    newConsigneeAddress1,
    newConsigneeAddress2,
    newConsigneeCity,
    newConsigneeState,
    newConsigneeZip,
  ],
  () => {
    if (!newConsigneeShippingSameAsConsignee.value) return
    newConsigneeShippingAddress1.value = newConsigneeAddress1.value
    newConsigneeShippingAddress2.value = newConsigneeAddress2.value
    newConsigneeShippingCity.value = newConsigneeCity.value
    newConsigneeShippingState.value = newConsigneeState.value
    newConsigneeShippingZip.value = newConsigneeZip.value
  },
  { immediate: true },
)

function goToConsignees() {
  emit('close')
  router.push('/consignees')
}

async function createOrg() {
  if (!newOrgName.value) return
  const { error } = await supabase
    .schema('app' as any).from('organizations')
    .insert({ name: newOrgName.value, type: newOrgType.value })
  if (!error) {
    inlineCreateOrg.value = false
    newOrgName.value = ''
    await loadOrgs()
  }
}
async function createContact() {
  if (!newContactName.value) return
  const { error } = await supabase
    .schema('app' as any).from('contacts')
    .insert({
      name: newContactName.value,
      email: newContactEmail.value,
      phone: newContactPhone.value,
      organization_id: organizationId.value || null,
    })
  if (!error) {
    inlineCreateContact.value = false
    newContactName.value = ''
    newContactEmail.value = ''
    newContactPhone.value = ''
    await loadContacts()
  }
}
</script>
